import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import type { PartialId } from '@attain-apps/string-builder'

import * as state from '../../../../../../../state'
import * as assistant from '../../../../assistant'

@ngCore.Component({
  host: {
    class: 'atn-chat-message-list',
  },
  imports: [
    assistant.conversation.loader.Component,
    assistant.conversation.message.Component,
    assistant.conversation.emptyState.Component,
  ],
  selector: 'atn-chat-message-list',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Host element reference for auto-scrolling */
  private readonly _hostRef = ngCore.inject<ngCore.ElementRef<HTMLElement>>(
    ngCore.ElementRef,
  )
  /** @see {@link state.assistant.conversational.Group} */
  public readonly group = ngCore.inject(state.assistant.conversational.Group)
  /** @see {@link state.fileMgmt.Feature} */
  public readonly fileMgmtFeature = ngCore.inject(state.fileMgmt.Feature)
  /** @see {@link state.fileMgmt.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /**
   * Gets messages for the current conversation, each with the entries it is
   * made of.
   */
  public readonly messagesSg = ngCore.computed(
    () =>
      this.group.messages.plugins
        .lookup(state.assistant.conversational.messages.plugins.sender.Plugin)
        .get()
        .feature.selectors.messagesWithInfo.asSignal() ?? [],
  )

  /**
   * Effect to auto-scroll to bottom when new messages are added
   */
  private readonly _autoScrollEffect = ngCore.effect(() => {
    // we need to read messagesSg to trigger on new messages / message updates
    this.messagesSg()

    requestAnimationFrame(() => {
      this._scrollToBottom()
    })
  })

  /**
   * Scroll parent container to bottom (parent has overflow-y: auto)
   */
  private _scrollToBottom(): void {
    const parent = this._hostRef.nativeElement?.parentElement
    if (parent) {
      parent.scrollTop = parent.scrollHeight
    }
  }

  /** Handle file download request */
  public fileDownloadClickHandler(event: {
    message: ents.core.agent.Message
    file: ents.core.agent.Message.File
  }): void {
    const conversationId =
      this.group.conversation.plugins.currentEntityPlugin.feature.selectors
        .getId()
        .asSignal()
    const context = this.meFeature.contexts.selectors.zoneContext.asSignal()
    if (!conversationId || !context) {
      return
    }
    const { extension, name: id } =
      state.fileMgmt.models.FileRequest.getFileInfo(event.file)
    this.fileMgmtFeature.actions.download
      .from('Assistant.Conversational.Message.FileDownloadClicked')
      .dispatch({
        context,
        extension,
        id,
        purpose: ents.core.common.fileManagement.Purpose.FileManagement,
        qualifier: ents.core.agent.Message.File.generateQualifier({
          conversation: conversationId as PartialId,
          message: event.message,
        }),
      })
  }

  /** Handle retry message request */
  public retryMessageHandler(message: ents.core.agent.Message): void {
    this.group.messages.plugins
      .lookup(state.assistant.conversational.messages.plugins.sender.Plugin)
      .get()
      .feature.actions.retryMessage.from(
        'DolomiteGeneral.ChatPage.retryMessage',
      )
      .dispatch({
        messageId: message.id,
      })
  }
}
